/**
 * The web editor's Tiptap extension list (#1106), built per toolbar set
 * (#1530 Teams parity, owner 2026-10-05).
 *
 * ⚠️ THE MESSAGE SET MUST NOT PRODUCE WHAT THE SERVER DROPS. The message
 * rule (`correspondenceSanitizer.ts`, `MESSAGE_DROPPED_TAGS` / `_STYLES`)
 * drops `sub`, `sup`, `text-align` and `font-family`. Hiding the buttons is
 * not enough: an extension that is loaded still answers its keyboard chord
 * (`Mod-.` superscript, `Mod-,` subscript, `Mod-Shift-E` centre) and still
 * parses a pasted `<sup>`, so the author would see formatting the server
 * then strips. With `toolbarSet === 'message'` those four extensions are
 * not loaded at all: the chord is dead, the paste is flattened on entry.
 * Pinned with a real editor in `rich-text-editor-message-set.web.test.ts`.
 *
 * #1599 Story 1.4: the message set also carries the mention token and its
 * `@` suggester (`siteScopeMention.ts`) when the caller hands one in. The
 * full (request-field) set never does: a request field has no audience to
 * mention into. Pinned in `site-scope-mention.web.test.ts`.
 */
import Color from '@tiptap/extension-color';
import FontFamily from '@tiptap/extension-font-family';
import Highlight from '@tiptap/extension-highlight';
import Subscript from '@tiptap/extension-subscript';
import Superscript from '@tiptap/extension-superscript';
import TableRow from '@tiptap/extension-table-row';
import TextAlign from '@tiptap/extension-text-align';
import { TextStyle } from '@tiptap/extension-text-style';
import Underline from '@tiptap/extension-underline';
import StarterKit from '@tiptap/starter-kit';

import {
  RICH_HTML_IMG_MAX_HEIGHT,
  RICH_HTML_IMG_MAX_WIDTH,
} from './richHtmlVisualContract';
import { SiteScopeLink } from './richTextAutoLink';
import { SiteScopeImage } from './siteScopeImage';
import {
  buildSiteScopeMention,
  type MentionSuggestSource,
  type SuggesterUiHandle,
} from './siteScopeMention';
import {
  SiteScopeTable,
  SiteScopeTableCell,
  SiteScopeTableHeader,
} from './siteScopeTable';
import { FontSize } from './tiptapFontSize';

import type { TextAreaToolbarSet } from '../textArea/TextAreaToolbar';
import type { Extensions } from '@tiptap/core';
import type { MutableRefObject } from 'react';

/** The mention wiring one message editor carries (#1599 Story 1.4). */
export interface WebEditorMention {
  source: MutableRefObject<MentionSuggestSource>;
  ui: SuggesterUiHandle;
}

export function buildWebEditorExtensions(
  toolbarSet: TextAreaToolbarSet = 'full',
  mention?: WebEditorMention,
): Extensions {
  const isMessageSet = toolbarSet === 'message';
  return [
    StarterKit.configure({ link: false }),
    Underline,
    TextStyle,
    Color,
    FontSize,
    ...(isMessageSet ? [] : [FontFamily]),
    Highlight.configure({ multicolor: true }),
    ...(isMessageSet ? [] : [Subscript, Superscript]),
    SiteScopeLink,
    ...(isMessageSet
      ? []
      : [TextAlign.configure({ types: ['heading', 'paragraph'] })]),
    SiteScopeImage.configure({
      allowBase64: false,
      HTMLAttributes: {
        style: `max-width: ${RICH_HTML_IMG_MAX_WIDTH}px; max-height: ${RICH_HTML_IMG_MAX_HEIGHT}px; width: auto; height: auto; display: inline-block;`,
      },
    }),
    SiteScopeTable.configure({ resizable: false }),
    TableRow,
    SiteScopeTableHeader,
    SiteScopeTableCell,
    ...(isMessageSet && mention
      ? [buildSiteScopeMention(mention.source, mention.ui)]
      : []),
  ];
}
